import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scissors, BedDouble, ArrowLeft, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr, todayStr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TIER_PRICE = { small: 1, medium: 1.25, large: 1.5, giant: 1.75 };

export default function Book() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState(null);
  const [services, setServices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [pets, setPets] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [checkIn, setCheckIn] = useState(todayStr());
  const [checkOut, setCheckOut] = useState("");
  const [availRooms, setAvailRooms] = useState(null);
  const [roomId, setRoomId] = useState("");
  const [petId, setPetId] = useState("");
  const [notes, setNotes] = useState("");
  const [petDialog, setPetDialog] = useState(false);
  const [petForm, setPetForm] = useState({ name: "", species: "Dog", breed: "", weight_kg: "", coat_type: "short", vaccine_notes: "", allergies: "", notes: "" });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/public/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/public/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "customer") {
      api.get("/portal/pets").then((r) => setPets(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    setSlot("");
    if (type === "grooming" && serviceId && date) {
      api.get("/public/slots", { params: { date, service_id: serviceId } })
        .then((r) => setSlots(r.data)).catch((e) => toast.error(fmtErr(e)));
    }
  }, [type, serviceId, date]);

  useEffect(() => {
    setAvailRooms(null);
    setRoomId("");
    if (type === "boarding" && checkIn && checkOut && checkOut > checkIn) {
      api.get("/public/rooms/available", { params: { check_in: checkIn, check_out: checkOut } })
        .then((r) => setAvailRooms(r.data)).catch((e) => toast.error(fmtErr(e)));
    }
  }, [type, checkIn, checkOut]);

  const service = services.find((s) => s.id === serviceId);
  const pet = pets.find((p) => p.id === petId);
  const room = (availRooms || rooms).find((r) => r.id === roomId);
  const nights = checkIn && checkOut ? Math.max(0, (new Date(checkOut) - new Date(checkIn)) / 86400000) : 0;
  const estPrice = useMemo(() => {
    if (type === "grooming" && service && pet) {
      let p = service.base_price * (TIER_PRICE[pet.weight_tier] || 1);
      if (["long", "matted", "double"].includes(pet.coat_type)) p *= 1.2;
      return p;
    }
    if (type === "boarding" && room && nights) return room.daily_rate * nights;
    return null;
  }, [type, service, pet, room, nights]);

  const addPet = async () => {
    try {
      const { data } = await api.post("/portal/pets", { ...petForm, weight_kg: parseFloat(petForm.weight_kg) || 0 });
      setPets([...pets, data]);
      setPetId(data.id);
      setPetDialog(false);
      toast.success(`${data.name} added to your pack`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submit = async () => {
    if (!user) {
      toast.info("Please sign in or create an account to book");
      navigate("/login?next=/book");
      return;
    }
    setSubmitting(true);
    try {
      const payload = type === "grooming"
        ? { type, pet_id: petId, service_id: serviceId, date, time: slot, notes }
        : { type, pet_id: petId, room_id: roomId, check_in: checkIn, check_out: checkOut, notes };
      const { data } = await api.post("/public/bookings", payload);
      setDone(data);
      toast.success("Booking confirmed! A confirmation email is on its way.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center animate-rise">
        <CheckCircle2 className="w-16 h-16 text-[#16A34A] mx-auto mb-4" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="booking-success-title">Booking confirmed!</h1>
        <p className="text-slate-600 mt-2">
          {done.service_name ? `${done.service_name} for ${done.pet_name} on ${done.date} at ${done.time}` :
            `${done.room_name} for ${done.pet_name}, ${done.check_in} → ${done.check_out} (${done.nights} night/s)`}
        </p>
        {done.price != null && <p className="font-display font-bold text-[#0E7C84] text-xl mt-2">{peso(done.price ?? done.estimated_total)}</p>}
        <div className="flex gap-3 justify-center mt-6">
          <Button className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => navigate("/portal")} data-testid="goto-portal-btn">View in My Portal</Button>
          <Button variant="outline" onClick={() => { setDone(null); setType(null); setServiceId(""); setRoomId(""); setSlot(""); }} data-testid="book-another-btn">Book another</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Book a service</h1>
      <p className="text-sm text-slate-500 mb-8">Real-time slots synced with our in-store calendar — no double bookings.</p>

      {!type ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <button onClick={() => setType("grooming")} className="bg-white border-2 border-[#BFE8E9] hover:border-[#0E7C84] rounded-2xl p-8 text-left hover-lift" data-testid="book-type-grooming">
            <span className="w-12 h-12 rounded-2xl bg-[#E84855] flex items-center justify-center mb-4"><Scissors className="w-6 h-6 text-white" /></span>
            <h3 className="font-display font-bold text-xl">Grooming</h3>
            <p className="text-sm text-slate-500 mt-1">Baths, full grooms, trims &amp; spa — pick your slot.</p>
          </button>
          <button onClick={() => setType("boarding")} className="bg-white border-2 border-[#BFE8E9] hover:border-[#0E7C84] rounded-2xl p-8 text-left hover-lift" data-testid="book-type-boarding">
            <span className="w-12 h-12 rounded-2xl bg-[#2A9D8F] flex items-center justify-center mb-4"><BedDouble className="w-6 h-6 text-white" /></span>
            <h3 className="font-display font-bold text-xl">Boarding</h3>
            <p className="text-sm text-slate-500 mt-1">Suites, kennels &amp; cat condos with daily care logs.</p>
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-6 space-y-6 animate-rise">
          <button className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#0E7C84]" onClick={() => setType(null)} data-testid="book-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {type === "grooming" && (
            <>
              <div>
                <Label className="mb-2 block">Choose a service</Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {services.map((s) => (
                    <button key={s.id} onClick={() => setServiceId(s.id)} data-testid={`service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`text-left border rounded-xl p-3 transition-colors ${serviceId === s.id ? "border-[#0E7C84] bg-[#D9F7F8]" : "hover:border-[#0E7C84]/50"}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-sm">{s.name}</span>
                        <Badge variant="secondary" className="text-[10px]">{s.tier}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                      <p className="text-sm font-display font-bold text-[#0E7C84] mt-1">from {peso(s.base_price)} · {s.duration_min} min</p>
                    </button>
                  ))}
                </div>
              </div>
              {serviceId && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date" />
                  </div>
                  <div>
                    <Label className="mb-2 block">Time slot</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((s) => (
                        <button key={s.time} disabled={!s.available} onClick={() => setSlot(s.time)} data-testid={`slot-${s.time}`}
                          className={`py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${slot === s.time ? "bg-[#0E7C84] text-white border-[#0E7C84]" : "hover:border-[#0E7C84]"}`}>
                          {s.time}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {type === "boarding" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Check-in</Label><Input type="date" min={todayStr()} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="checkin-date" /></div>
                <div><Label>Check-out</Label><Input type="date" min={checkIn} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="checkout-date" /></div>
              </div>
              {availRooms && (
                <div>
                  <Label className="mb-2 block">Available rooms ({availRooms.length})</Label>
                  {availRooms.length === 0 && <p className="text-sm text-amber-600">Fully booked for those dates — try different dates.</p>}
                  <div className="grid sm:grid-cols-2 gap-2">
                    {availRooms.map((r) => (
                      <button key={r.id} onClick={() => setRoomId(r.id)} data-testid={`room-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`text-left border rounded-xl p-3 transition-colors ${roomId === r.id ? "border-[#2A9D8F] bg-teal-50" : "hover:border-[#2A9D8F]/50"}`}>
                        <div className="flex justify-between"><span className="font-semibold text-sm">{r.name}</span><Badge variant="secondary" className="text-[10px] capitalize">{r.type}</Badge></div>
                        <p className="text-sm font-display font-bold text-[#2A9D8F] mt-1">{peso(r.daily_rate)} / night</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Which pet?</Label>
              <button className="text-xs text-[#0E7C84] font-semibold inline-flex items-center gap-1" onClick={() => user ? setPetDialog(true) : navigate("/login?next=/book")} data-testid="add-pet-btn">
                <Plus className="w-3.5 h-3.5" /> Add pet
              </button>
            </div>
            {pets.length === 0 && <p className="text-sm text-slate-500">{user ? "No pets yet — add one to continue." : "Sign in to select your pets."}</p>}
            <div className="grid sm:grid-cols-2 gap-2">
              {pets.map((p) => (
                <button key={p.id} onClick={() => setPetId(p.id)} data-testid={`pet-${p.name.toLowerCase()}`}
                  className={`text-left border rounded-xl p-3 transition-colors ${petId === p.id ? "border-[#0E7C84] bg-[#D9F7F8]" : "hover:border-[#0E7C84]/50"}`}>
                  <p className="font-semibold text-sm">{p.name} <span className="text-slate-400 font-normal">· {p.species}</span></p>
                  <p className="text-xs text-slate-500">{p.breed} · {p.weight_kg}kg · {p.weight_tier}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Notes (allergies, handling, requests)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="booking-notes" />
          </div>

          {estPrice != null && (
            <div className="bg-[#E2F5F5] rounded-xl p-4 flex justify-between items-center">
              <span className="text-sm text-slate-600">Estimated {type === "boarding" ? `(${nights} night/s)` : "price"}</span>
              <span className="font-display font-bold text-xl text-[#0E7C84]" data-testid="booking-est-price">{peso(estPrice)}</span>
            </div>
          )}

          <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" size="lg" onClick={submit}
            disabled={submitting || !petId || (type === "grooming" ? !serviceId || !slot : !roomId)} data-testid="confirm-booking-btn">
            {submitting ? "Confirming…" : "Confirm Booking"}
          </Button>
        </div>
      )}

      <Dialog open={petDialog} onOpenChange={setPetDialog}>
        <DialogContent data-testid="add-pet-dialog">
          <DialogHeader><DialogTitle className="font-display">Add a pet</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={petForm.name} onChange={(e) => setPetForm({ ...petForm, name: e.target.value })} data-testid="pet-name" /></div>
            <div>
              <Label>Species</Label>
              <Select value={petForm.species} onValueChange={(v) => setPetForm({ ...petForm, species: v })}>
                <SelectTrigger data-testid="pet-species"><SelectValue /></SelectTrigger>
                <SelectContent>{["Dog", "Cat", "Bird", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Breed</Label><Input value={petForm.breed} onChange={(e) => setPetForm({ ...petForm, breed: e.target.value })} data-testid="pet-breed" /></div>
            <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={petForm.weight_kg} onChange={(e) => setPetForm({ ...petForm, weight_kg: e.target.value })} data-testid="pet-weight" /></div>
            <div>
              <Label>Coat type</Label>
              <Select value={petForm.coat_type} onValueChange={(v) => setPetForm({ ...petForm, coat_type: v })}>
                <SelectTrigger data-testid="pet-coat"><SelectValue /></SelectTrigger>
                <SelectContent>{["short", "long", "double", "matted", "hairless"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vaccination records</Label><Input value={petForm.vaccine_notes} onChange={(e) => setPetForm({ ...petForm, vaccine_notes: e.target.value })} placeholder="e.g. Anti-rabies until 2027" data-testid="pet-vaccines" /></div>
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={addPet} disabled={!petForm.name} data-testid="save-pet-btn">Save pet</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
